import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, FileText, Loader2, RefreshCw, ScanLine, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  controlledDocumentsApi,
  type ControlledDocumentFile,
  type ControlledDocumentListItem,
} from '@/api/controlled-documents';

const STATUS_LABELS: Record<string, string> = {
  AWAITING_SUBMISSION: 'Ожидает сдачи',
  SUBMITTED: 'Сдан',
  ACCEPTED: 'Принят',
  ARCHIVED: 'В архиве',
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function statusClass(status?: string | null) {
  if (status === 'AWAITING_SUBMISSION') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (status === 'SUBMITTED') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (status === 'ACCEPTED') return 'border-green-200 bg-green-50 text-green-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
}

function ownerLabel(item: ControlledDocumentListItem) {
  return item.employee?.name || 'Без владельца';
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function DocumentCard({
  item,
  onOpen,
}: {
  item: ControlledDocumentListItem;
  onOpen: (item: ControlledDocumentListItem) => void;
}) {
  return (
    <article
      className="rounded-lg border border-border bg-card p-3 active:bg-accent"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(item);
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-medium leading-snug">{item.title}</h2>
          <p className="mt-1 truncate text-sm text-muted-foreground">{ownerLabel(item)}</p>
        </div>
        <FileText className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Badge variant="outline" className={statusClass(item.lifecycleStatus)}>
          {STATUS_LABELS[item.lifecycleStatus || ''] || item.lifecycleStatus || '—'}
        </Badge>
        <Badge variant="outline">{item.typeTitle}</Badge>
      </div>
      <div className="mt-3 font-mono text-xs text-blue-700">{item.currentBarcode}</div>
      <div className="mt-1 text-xs text-muted-foreground">Срок: {formatDate(item.dueAt)}</div>
    </article>
  );
}

function DocumentSheet({
  itemId,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['mobile-controlled-document-detail', itemId],
    queryFn: () => controlledDocumentsApi.detail(itemId!),
    enabled: open && Boolean(itemId),
  });

  const submitMutation = useMutation({
    mutationFn: () => {
      const barcode = data?.currentVersion?.barcode;
      if (!barcode) throw new Error('Нет текущей версии');
      return controlledDocumentsApi.submit(barcode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-controlled-documents'] });
      queryClient.invalidateQueries({ queryKey: ['mobile-controlled-document-detail', itemId] });
      toast.success('Документ отмечен как сданный');
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Не удалось сдать документ'),
  });

  const downloadFile = async (file: ControlledDocumentFile) => {
    setDownloadingFileId(file.id);
    try {
      const blob = await controlledDocumentsApi.downloadFile(file.id);
      downloadBlob(blob, file.originalName || file.storagePath.split('/').pop() || 'document');
    } finally {
      setDownloadingFileId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{data?.title || 'Документ'}</SheetTitle>
          <SheetDescription>{data ? `${ownerLabel(data)} · ${data.currentBarcode}` : ''}</SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="py-10 text-center">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {data && (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className={statusClass(data.lifecycleStatus)}>
                {STATUS_LABELS[data.lifecycleStatus || ''] || data.lifecycleStatus || '—'}
              </Badge>
              <Badge variant="outline">{data.typeTitle}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Срок</div>
                <div>{formatDate(data.dueAt)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Версий</div>
                <div>{data.versions.length}</div>
              </div>
            </div>

            {data.currentVersion?.lifecycleStatus === 'AWAITING_SUBMISSION' && (
              <Button className="w-full" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
                {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Сдать документ
              </Button>
            )}

            <div>
              <h3 className="mb-2 text-sm font-medium">Файлы текущей версии</h3>
              <div className="space-y-2">
                {(data.currentVersion?.files || []).map((file) => (
                  <button
                    type="button"
                    key={file.id}
                    className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm"
                    onClick={() => downloadFile(file)}
                    disabled={downloadingFileId === file.id}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{file.originalName || file.storagePath.split('/').pop()}</span>
                      <span className="block text-xs text-muted-foreground">{file.format} · {formatDate(file.generatedAt)}</span>
                    </span>
                    {downloadingFileId === file.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <Download className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                ))}
                {(data.currentVersion?.files || []).length === 0 && (
                  <div className="text-sm text-muted-foreground">Файлов нет</div>
                )}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium">История</h3>
              <div className="space-y-2">
                {data.events.map((event) => (
                  <div key={event.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>{event.eventType}</span>
                      <span>{formatDate(event.createdAt)}</span>
                    </div>
                    {event.message && <div className="mt-1">{event.message}</div>}
                  </div>
                ))}
                {data.events.length === 0 && <div className="text-sm text-muted-foreground">История пуста</div>}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function ControlledDocumentsPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const params = useMemo(() => ({
    status: status || undefined,
    search: search || undefined,
    scope: 'mine',
    limit: 80,
  }), [status, search]);

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['mobile-controlled-documents', params],
    queryFn: () => controlledDocumentsApi.list(params),
  });

  const scanSubmitMutation = useMutation({
    mutationFn: async (rawBarcode: string) => {
      const barcode = rawBarcode.trim().toUpperCase();
      if (!barcode) throw new Error('Штрихкод пустой');
      const lookup = await controlledDocumentsApi.lookup(barcode);
      if (!lookup.isCurrent) {
        const currentBarcode = lookup.currentVersion?.barcode ?? lookup.document.currentBarcode;
        throw new Error(`Это неактуальная версия документа. Сдавать нужно документ со штрихкодом ${currentBarcode}.`);
      }
      const status = lookup.currentVersion?.lifecycleStatus ?? lookup.version.lifecycleStatus;
      if (status === 'SUBMITTED') {
        return { already: true, title: lookup.document.title };
      }
      if (status !== 'AWAITING_SUBMISSION') {
        throw new Error(`Документ нельзя сдать из статуса “${STATUS_LABELS[status || ''] || status || '—'}”.`);
      }
      await controlledDocumentsApi.submit(lookup.barcode);
      return { already: false, title: lookup.document.title };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['mobile-controlled-documents'] });
      toast.success(result.already ? 'Документ уже был сдан' : 'Документ отмечен как сданный');
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Не удалось сдать документ'),
  });

  const openItem = (item: ControlledDocumentListItem) => {
    setSelectedId(item.id);
    setSheetOpen(true);
  };

  const items = data?.items ?? [];

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Документы"
        showBack
        backTo="/more"
        rightContent={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setScannerOpen(true)}
              disabled={scanSubmitMutation.isPending}
              title="Сдать документ по штрихкоду"
            >
              {scanSubmitMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isRefetching}>
              {isRefetching ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
            </Button>
          </div>
        }
      />

      <main className="space-y-4 p-4 pb-24">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск по штрихкоду или названию"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            ['', 'Все'],
            ['AWAITING_SUBMISSION', 'Ожидают'],
            ['SUBMITTED', 'Сданы'],
            ['ACCEPTED', 'Приняты'],
            ['ARCHIVED', 'Архив'],
          ].map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={status === value ? 'default' : 'outline'}
              className={cn('shrink-0')}
              onClick={() => setStatus(value)}
            >
              {label}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="py-12 text-center">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Документов нет
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <DocumentCard key={item.id} item={item} onOpen={openItem} />
            ))}
          </div>
        )}
      </main>

      <DocumentSheet itemId={selectedId} open={sheetOpen} onOpenChange={setSheetOpen} />
      {scannerOpen && (
        <BarcodeScanner
          onScan={(nextBarcode) => {
            setScannerOpen(false);
            scanSubmitMutation.mutate(nextBarcode);
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </div>
  );
}
